import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '../hooks/useAuth';
import AuthGuard from '../components/AuthGuard';
import ScrollToTop from '../components/ScrollToTop'; 

// Import pages
import Landing from '../pages/landing';
import Signup from '../pages/Signup';
import Login from '../pages/Login';
import EmailVerification from '../pages/EmailVerification';
import PasswordResetRequest from '../pages/PasswordResetRequest';
import PasswordResetConfirm from '../pages/PasswordResetConfirm';
import DataTest from '../pages/dataTest';
import Start from '../pages/Start';
import NewProject from '../pages/NewProject';
import StyleChoices from '../pages/StyleChoices';
import ProjectDetails from '../pages/ProjectDetails';
import CreateView from '../pages/CreateView';
import PromptStatus1 from '../pages/PromptStatus1';
import PromptStatus2 from '../pages/PromptStatus2';
import Clarifications from '../pages/Clarifications';
import ViewDetails from '../pages/ViewDetails';
import AllProjects from '../pages/AllProjects';
import ReviseView from '../pages/ReviseView';
import PromptStatus3 from '../pages/PromptStatus3';
import BuildFlow from '../pages/BuildFlow';
import ViewFlow from '../pages/ViewFlow';
import Account from '../pages/Account';
import PaymentCancelPage from '../pages/PaymentCancelPage';
import PaymentSuccessPage from '../pages/PaymentSuccessPage';
import PaymentPage from '../pages/PaymentPage';
import HelpCenter from '../pages/HelpCenter';
import SharedContent from '../pages/SharedContent';
import Features from '../pages/Features';
import Pricing from '../pages/Pricing';
import Roadmap from '../pages/Roadmap';
import Changelog from '../pages/Changelog';
import Contact from '../pages/Contact';
import Privacy from '../pages/Privacy';
import Terms from '../pages/Terms';
import About from '../pages/About';
import PublicHelpCenter from '../pages/PublicHelpCenter';
import Blog from '../pages/Blog';
import BlogPost from '../pages/BlogPost';
import DesignersUseCase from '../pages/DesignersUseCase';
import ProductManagersUseCase from '../pages/ProductManagersUsecase';
import StartupsUseCase from '../pages/StartupsUseCase';
import CreateEnterprise from '../pages/CreateEnterprise';
import EnterpriseSuccess from '../pages/EnterpriseSuccess';
import EnterpriseCancel from '../pages/EnterpriseCancel';
import EditFlow from '../pages/EditFlow';
import AddMedia from '../pages/AddMedia';
import Demo from '../pages/Demo';
import SharedWithMe from '../pages/SharedWithMe';
import EnterpriseUserData from '../pages/EnterpriseUserData';
import EnterpriseViewData from '../pages/EnterpriseViewData';
import EnterpriseGuide from '../components/EnterpriseGuide';

const AppRouter = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/" element={<Landing />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/password-reset" element={<PasswordResetRequest />} />
            <Route path="/password-reset-confirm" element={<PasswordResetConfirm />} />
            <Route path="/shared/:shareId" element={<SharedContent />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/help" element={<PublicHelpCenter />} />
            <Route path="/enterprise-guide" element={<EnterpriseGuide />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/use-cases/designers" element={<DesignersUseCase />} />
            <Route path="/use-cases/product-managers" element={<ProductManagersUseCase />} />
            <Route path="/use-cases/startups" element={<StartupsUseCase />} />
            <Route path="/demo" element={<Demo />} />

            {/* Protected routes - require authentication */}
            <Route path="/add-media" element={<AuthGuard><AddMedia /></AuthGuard>} />
            <Route path="/user/help" element={<AuthGuard><HelpCenter /></AuthGuard>} />
            <Route path="/test" element={<AuthGuard><DataTest /></AuthGuard>} />
            <Route path="/start" element={<AuthGuard><Start /></AuthGuard>} />
            <Route path="/projects" element={<AuthGuard><AllProjects /></AuthGuard>} />
            <Route path="/shared-with-me" element={<AuthGuard><SharedWithMe /></AuthGuard>} />
            <Route path="/account" element={<AuthGuard><Account /></AuthGuard>} />
            <Route path="/new-project" element={<AuthGuard><NewProject /></AuthGuard>} />
            <Route path="/style-choices" element={<AuthGuard><StyleChoices /></AuthGuard>} />
            <Route path="/project-details" element={<AuthGuard><ProjectDetails /></AuthGuard>} />
            <Route path="/create-view" element={<AuthGuard><CreateView /></AuthGuard>} />
            <Route path="/revise-view" element={<AuthGuard><ReviseView /></AuthGuard>} />
            <Route path="/create-flow" element={<AuthGuard><BuildFlow /></AuthGuard>} />
            <Route path="/view-flow" element={<AuthGuard><ViewFlow /></AuthGuard>} />
            <Route path="/edit-flow" element={<AuthGuard><EditFlow /></AuthGuard>} />
            <Route path="/prompt-status" element={<AuthGuard><PromptStatus1 /></AuthGuard>} />
            <Route path="/prompt-status-2" element={<AuthGuard><PromptStatus2 /></AuthGuard>} />
            <Route path="/prompt-status-3" element={<AuthGuard><PromptStatus3 /></AuthGuard>} />
            <Route path="/clarifications" element={<AuthGuard><Clarifications /></AuthGuard>} />
            <Route path="/view-details" element={<AuthGuard><ViewDetails /></AuthGuard>} />
            <Route path="/enterprise/create" element={<AuthGuard><CreateEnterprise /></AuthGuard>} />
            <Route path="/enterprise/success" element={<AuthGuard><EnterpriseSuccess /></AuthGuard>} />
            <Route path="/enterprise/cancel" element={<AuthGuard><EnterpriseCancel /></AuthGuard>} />
            <Route path="/enterprise/user-data" element={<AuthGuard><EnterpriseUserData /></AuthGuard>} />
            <Route path="/enterprise-view-data" element={<AuthGuard><EnterpriseViewData /></AuthGuard>} />

            {/* Semi-protected routes - require auth check within the component */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-cancel" element={<PaymentCancelPage />} />
            <Route path="/payment" element={<AuthGuard><PaymentPage /></AuthGuard>} />
          </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRouter;